import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  createThread as createThreadService,
  getThreads as getThreadsService,
  getThreadMessages as getThreadMessagesService,
  getThreadDetails as getThreadDetailsService,
  sendMessage as sendMessageService,
  archiveThread as archiveThreadService
} from '../../services/messagingService';

const initialState = {
  threads: [],
  currentThread: null,
  messages: [],
  loading: false,
  error: null
};

// Async thunks
export const createThread = createAsyncThunk(
  'messaging/createThread',
  async ({ subject, initialMessage, participants = [] }) => {
    const threadData = await createThreadService({
      subject,
      initialMessage,
      participants
    });
    
    return { 
      id: threadData.id, 
      ...threadData,
      messages: threadData.messages || []
    };
  }
);

export const fetchThreads = createAsyncThunk(
  'messaging/fetchThreads',
  async () => {
    const threads = await getThreadsService();
    return threads;
  }
);

export const fetchThreadMessages = createAsyncThunk(
  'messaging/fetchThreadMessages',
  async (threadId) => {
    const result = await getThreadMessagesService(threadId);
    return result;
  }
);

export const fetchThreadDetails = createAsyncThunk(
  'messaging/fetchThreadDetails',
  async (threadId) => {
    const thread = await getThreadDetailsService(threadId);
    return thread;
  }
);

export const sendMessage = createAsyncThunk(
  'messaging/sendMessage',
  async ({ threadId, content }) => {
    const message = await sendMessageService(threadId, content);
    return message;
  }
);

export const archiveThread = createAsyncThunk(
  'messaging/archiveThread',
  async (threadId) => {
    await archiveThreadService(threadId);
    return threadId;
  }
);

const messagingSlice = createSlice({
  name: 'messaging',
  initialState,
  reducers: {
    clearCurrentThread: (state) => {
      state.currentThread = null;
      state.messages = [];
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder
      // Create Thread
      .addCase(createThread.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createThread.fulfilled, (state, action) => {
        state.threads.unshift(action.payload);
        state.currentThread = action.payload;
        state.loading = false;
      })
      .addCase(createThread.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      })

      // Fetch Threads
      .addCase(fetchThreads.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchThreads.fulfilled, (state, action) => {
        state.threads = action.payload;
        state.loading = false;
      })
      .addCase(fetchThreads.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      })

      // Fetch Thread Messages
      .addCase(fetchThreadMessages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchThreadMessages.fulfilled, (state, action) => {
        state.currentThread = action.payload.thread;
        state.messages = action.payload.messages;
        state.loading = false;
      })
      .addCase(fetchThreadMessages.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      })

      // Fetch Thread Details
      .addCase(fetchThreadDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchThreadDetails.fulfilled, (state, action) => {
        state.currentThread = action.payload;
        state.loading = false;
      })
      .addCase(fetchThreadDetails.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      })

      // Send Message
      .addCase(sendMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.messages.push(action.payload);
        if (state.currentThread) {
          state.currentThread.lastMessageAt = action.payload.sentAt;
        }
        state.loading = false;
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      })

      // Archive Thread
      .addCase(archiveThread.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(archiveThread.fulfilled, (state, action) => {
        state.threads = state.threads.filter(thread => thread.id !== action.payload);
        if (state.currentThread?.id === action.payload) {
          state.currentThread = null;
        }
        state.loading = false;
      })
      .addCase(archiveThread.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      });
  }
});

export const { clearCurrentThread, setError } = messagingSlice.actions;

// Add selector
export const selectMessaging = (state) => state.messaging;

export default messagingSlice.reducer;
